import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import type { TProcessingAction, TPublisherSong } from '@modules/types'
import { StatedFields } from '@ds/components'
import { SongStatusBlock } from './SongStatusBlock'
import { SongActionButtons } from './SongActionButtons'

interface DraftReviewSong {
    songId: number
    isApproved: boolean | null
    copyrightNotice?: string | null
    rejectReason?: string | null
    isCounterOffered?: boolean
    printIsCounterOffered?: boolean
    printCounterOfferFee?: number
    digitalIsCounterOffered?: boolean
    digitalCounterOfferFee?: number
}

interface SongHeaderProps {
    onApprove: () => void
    onReject: (songId: number, reason: string) => void
    song: TPublisherSong
    processingSongId: number | null
    processingAction: TProcessingAction
    draftReviewSong: DraftReviewSong
    onChangeStatus: (songId: number) => void
    isLoading: boolean
    initialLoading: boolean
    isBlocked?: boolean
}

const getSongStatus = (
    draftReviewSong: DraftReviewSong,
): 'approved' | 'rejected' | 'counteroffer' | null => {
    if (draftReviewSong.isApproved === true) {
        return 'approved'
    }

    if (
        draftReviewSong.isCounterOffered ||
        draftReviewSong.printIsCounterOffered ||
        draftReviewSong.digitalIsCounterOffered ||
        draftReviewSong.rejectReason === 'Counteroffer'
    ) {
        return 'counteroffer'
    }

    if (draftReviewSong.isApproved === false) {
        return 'rejected'
    }

    return null
}

const shouldShowActionButtons = (
    draftReviewSong?: DraftReviewSong,
    hasApprovalDecision?: boolean,
    initialLoading?: boolean,
): boolean => {
    if (initialLoading) return true
    if (hasApprovalDecision) return true
    return !draftReviewSong || !draftReviewSong.songId || draftReviewSong.isApproved === null
}

const shouldShowStatusBlock = (
    draftReviewSong?: DraftReviewSong,
    hasApprovalDecision?: boolean,
    initialLoading?: boolean,
): boolean => {
    if (initialLoading) return false
    return Boolean(draftReviewSong && draftReviewSong.isApproved !== null && !hasApprovalDecision)
}

const isProcessingSong = (songId: number, processingSongId?: number | null): boolean => {
    return processingSongId === songId
}

const SONG_SHOWN_ABOVE = ['title', 'writers']

const SongInfo = memo(({ song }: { song: TPublisherSong }) => (
    <Box display="flex" flexDirection="column">
        <Typography variant="caption" color="text.secondary">
            {song.clientRequestSong?.writers}
        </Typography>
        <Typography variant="h5" fontWeight={600} flex={1} mb={0}>
            {song.clientRequestSong?.title}
        </Typography>
        {song.clientRequestSong && (
            <Box mt={1}>
                <StatedFields data={song.clientRequestSong} skip={SONG_SHOWN_ABOVE} size={12} />
            </Box>
        )}
    </Box>
))

const SongActions = memo(
    ({
        song,
        draftReviewSong,
        processingSongId,
        processingAction,
        onApprove,
        onReject,
        onChangeStatus,
        isLoading,
        initialLoading,
        isBlocked,
    }: {
        song: TPublisherSong
        draftReviewSong: DraftReviewSong
        processingSongId: number | null
        processingAction: TProcessingAction
        onApprove: (copyrightNotice?: string) => void
        onReject: (songId: number, reason: string, copyrightNotice?: string) => void
        onChangeStatus: (songId: number) => void
        isLoading: boolean
        initialLoading: boolean
        isBlocked?: boolean
    }) => {
        const hasApprovalDecision = Boolean(song.approvalDecision)
        const showActionButtons = shouldShowActionButtons(
            draftReviewSong,
            hasApprovalDecision,
            initialLoading,
        )
        const showStatusBlock = shouldShowStatusBlock(
            draftReviewSong,
            hasApprovalDecision,
            initialLoading,
        )
        const isProcessing = isProcessingSong(song.id, processingSongId)
        const songStatus = getSongStatus(draftReviewSong)

        return (
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                {showActionButtons && (
                    <SongActionButtons
                        onApprove={onApprove}
                        onReject={onReject}
                        songId={song.id}
                        isApproving={isProcessing && processingAction === 'approve'}
                        isRejecting={isProcessing && processingAction === 'reject'}
                        isLoading={isLoading}
                        initialLoading={initialLoading}
                        isBlocked={isBlocked}
                        processingSongId={processingSongId}
                        isCounteroffer={Boolean(song.counterOfferFees?.length)}
                        approvalDecision={song.approvalDecision}
                    />
                )}

                {showStatusBlock && draftReviewSong && (
                    <SongStatusBlock
                        status={songStatus}
                        songId={song.id}
                        initialLoading={initialLoading}
                        isReseting={isProcessing && processingAction === 'reset'}
                        onChangeStatus={onChangeStatus}
                        processingSongId={processingSongId}
                    />
                )}
            </Box>
        )
    },
)

export const SongHeader = memo(
    ({
        onApprove,
        onReject,
        song,
        processingSongId,
        processingAction,
        draftReviewSong,
        onChangeStatus,
        isLoading,
        initialLoading,
        isBlocked,
    }: SongHeaderProps) => {
        return (
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <SongInfo song={song} />
                <SongActions
                    song={song}
                    draftReviewSong={draftReviewSong}
                    processingSongId={processingSongId}
                    processingAction={processingAction}
                    onApprove={onApprove}
                    onReject={onReject}
                    onChangeStatus={onChangeStatus}
                    isLoading={isLoading}
                    initialLoading={initialLoading}
                    isBlocked={isBlocked}
                />
            </Box>
        )
    },
)
